import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { DefaultButton, MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { useHistory, useLocation } from "react-router-dom";
import DocumentTitle from "react-document-title";
import CustomIcon from "@/components/icons/CustomIcon";
import RoleFilterDropdown, { RoleFilterValue } from "@/screen/Identities/RoleFilterDropdown";
import { countByPersonType, filterByPersonType, PersonTypeTabs, shouldShowPersonTypeTabs } from "@/screen/Identities/personTypeTabRules";
import Auth from "@/Auth/Auth";
import UserIdentitiesService, { MainUserIdentityDto, type AccessRequestItemDto } from "@/services/UserIdentitiesService";
import savingsPlans from "@/services/SavingsPlanService";
import { UserIdentityStatusEnum } from "@/components/types/UserIdentity.Status";
import SignaturesService, { ISignature } from "@/services/SignaturesService";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { IdentityEditPath, IdentityMinorPath, IdentityNewPath, KycSignaturePath, SuitabilityTestRenewalPath, type SuitabilityTestRenewalNavigationState } from "@/screen/Identities/identitiesRoutes";
import { CompanyDetailsPath, CompanySubscriptionPath, isCompanyOnboardingEnabled } from "@/screen/Companies/companiesRoutes";
import type { CompanyDetailsNavigationState } from "@/screen/Companies/companiesRoutes";
import { isTransferPendingAcceptance } from "@/screen/Identities/IdentityForm/acceptTransferRules";
import { ContractOnboardingStrings, resolveContractOnboardingError } from "@/screen/Map/contractOnboarding.strings";
import { canAddAdditionalIntervener, isMainNewDraft, isMainSelectableForStep21, mergeMainIntoIdentitiesList, normalizeIdentityListItems, normalizeMainUserIdentityDto } from "@/screen/Identities/holderIdentityDraft";
import { getSuitabilityTestAction, getSuitabilityTestLabel } from "@/screen/Identities/suitabilityTestColumnRules";
// Interfaces
import { AccessRequestCurrentRow, AccessRequestHistoryRow, Identity, IdentityPersonType, IdentitiesMainScreenNavigationState, RevokeAccessResult } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import { AccessRequestDecisionOutcome, classifyAccessRequestDecisionFailure, currentRowToHistoryRow, formatAccessRequestStatusForDisplay, isUnresolvedAccessRequestStatus, mapAccessRequestItemToCurrentRow, resolveApprovedStatusCode } from "@/screen/Identities/accessRequestsMappers";
import { IdentityNavigationState } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { orchestratorErrorsToSingleMessage } from "@/screen/Identities/IdentityForm/identityServerErrors";
// Styles
import { HelperStyle, Cont, MainCont, Title, TitleSubtitleRow, BarCont, BgBar, TabletCont, Table, ButtonsContainer, RoleBadge, PendingTransferBadge, SavingsPlansLink, RoleFilterRow, PersonTypePivotWrap, SignKycIcon, ContractModeBanner, ContractModeBannerRow, ContractModeBannerText, ContractModeBannerActions } from "@/screen/Identities/Styles/IdentitiesMainScreenStyles";
import { AccessRequestsCountInline, accessRequestsTriggerButtonStyles } from "@/screen/Identities/Styles/ModalAccessRequestsStyles";
// Modals
import ModalAccessRequests from "@/screen/Identities/ModalAccessRequests";
import ModalDeleteIdentity from "@/screen/Identities/ModalDeleteIdentity";
import ModalRenounceAccess from "@/screen/Identities/ModalRenounceAccess";
import ModalSavingsPlans from "@/screen/Identities/ModalSavingsPlans";
import ModalAssociatedUsers from "@/screen/Identities/ModalAssociatedUsers";


const IdentitiesMainScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const history = useHistory();
    const location = useLocation<IdentitiesMainScreenNavigationState>();
    const contractSavingsPlanId = location.state?.contractSavingsPlanId;
    const isContractMode = contractSavingsPlanId != null;
    const [contractSavingsPlanName, setContractSavingsPlanName] = useState<string>("");
    // Paso actual del onboarding (fuente de verdad: backend). En modo contrato el botón "Continuar"
    // (avanza el paso) SOLO debe aparecer si el plan está realmente en 2.1; en cualquier otro paso el
    // listado sigue siendo visitable (p. ej. "Añadir o modificar" desde el mapa) pero sin avanzar nada.
    const [contractCurrentStep, setContractCurrentStep] = useState<string | null>(null);
    const [isResolvingContractStep, setIsResolvingContractStep] = useState<boolean>(isContractMode);
    // 2.1 = IntervenersManagement: único paso de este listado que avanza con advanceStep.
    // 2.4.1 (rama MinorToAdult) NO avanza aquí: lo cierra MinorToAdultHolder (FE-9), no advanceStep.
    const canAdvanceContractStep = contractCurrentStep === "2.1";
    // Sección 4: onboarding terminado (activo, caducado o cancelado), el aviso de contrato no aplica.
    const isContractOnboardingFinished = contractCurrentStep?.startsWith("4") === true;
    const showContractBanner = isContractMode && !isResolvingContractStep && !isContractOnboardingFinished;
    // Aviso de éxito al volver tras finalizar un alta/edición (se recibe por el state de navegación).
    const [savedSuccessMessage, setSavedSuccessMessage] = useState<string | null>(location.state?.savedSuccessMessage ?? null);
    const [suitabilityLimitMessage, setSuitabilityLimitMessage] = useState<string | null>(location.state?.suitabilityLimitMessage ?? null);
    const [advanceError, setAdvanceError] = useState<string | null>(null);
    const [isAdvancingStep, setIsAdvancingStep] = useState(false);
    const [identities, setIdentities] = useState<Identity[]>([]);
    const [mainIdentity, setMainIdentity] = useState<MainUserIdentityDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIdentity, setSelectedIdentity] = useState<Identity | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteModalError, setDeleteModalError] = useState<string | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [showRenounceModal, setShowRenounceModal] = useState(false);
    const [renounceModalError, setRenounceModalError] = useState<string | null>(null);
    const [renounceSubmitting, setRenounceSubmitting] = useState(false);
    const [showSavingsPlansModal, setShowSavingsPlansModal] = useState(false);
    const [showAssociatedUsersModal, setShowAssociatedUsersModal] = useState(false);
    const [showAccessRequestsModal, setShowAccessRequestsModal] = useState(false);
    const [allGuestRows, setAllGuestRows] = useState<AccessRequestCurrentRow[]>([]);
    const [allOwnRows, setAllOwnRows] = useState<AccessRequestCurrentRow[]>([]);
    const [accessRequestsLoading, setAccessRequestsLoading] = useState(true);
    const [accessRequestsLoadError, setAccessRequestsLoadError] = useState<string | null>(null);
    const [accessRequestDecisionSubmitting, setAccessRequestDecisionSubmitting] = useState(false);
    const [accessRequestDecisionError, setAccessRequestDecisionError] = useState<string | null>(null);
    const [accessRequestSignaturePhase, setAccessRequestSignaturePhase] = useState(false);
    const [accessRequestSignatureLoading, setAccessRequestSignatureLoading] = useState(false);
    const [accessRequestSignatures, setAccessRequestSignatures] = useState<ISignature[]>([]);
    const [accessRequestSignatureUserIdentity, setAccessRequestSignatureUserIdentity] = useState<MainUserIdentityDto | null>(null);
    const [accessRequestSignatureHeaderText, setAccessRequestSignatureHeaderText] = useState("");
    const [accessRequestDecisionRetryAvailable, setAccessRequestDecisionRetryAvailable] = useState(false);
    const pendingAccessRequestRowRef = useRef<AccessRequestCurrentRow | null>(null);
    const pendingAccessRequestStatusRef = useRef<"Approved" | "Rejected" | null>(null);
    // Firma con la que se resuelve la solicitud. Va en una ref y no en el estado porque el reintento tras un
    // fallo de red limpia la UI de firma (y con ella accessRequestSignatures), y la misma firma sigue siendo
    // válida: el backend comprueba que se firmó dentro de su ventana, no cuánto ha tardado en llegar.
    const pendingAccessRequestSignatureIdRef = useRef<number | null>(null);

    const processAccessRequestExpirationDate = (signs: ISignature[]) =>
    {
        if(signs === undefined || signs.length === 0)
        {
            return;
        }
        signs.forEach((signature: ISignature) =>
        {
            if(signature && typeof signature.expiresAt === "string")
            {
                signature.expiresAt = moment(signature.expiresAt).toDate();
            }
        });
    };

    const clearAccessRequestSignatureUi = useCallback(() =>
    {
        setAccessRequestSignaturePhase(false);
        setAccessRequestSignatureLoading(false);
        setAccessRequestSignatures([]);
        setAccessRequestSignatureUserIdentity(null);
        setAccessRequestSignatureHeaderText("");
    }, []);

    const resetAccessRequestSignatureState = useCallback(() =>
    {
        clearAccessRequestSignatureUi();
        setAccessRequestDecisionRetryAvailable(false);
        pendingAccessRequestRowRef.current = null;
        pendingAccessRequestStatusRef.current = null;
        pendingAccessRequestSignatureIdRef.current = null;
    }, [clearAccessRequestSignatureUi]);

    // Pendiente no es solo "Pending": una cesión de propiedad espera decisión en OwnershipTransferPending y
    // también tiene que salir como actuable. Mientras el filtro fue solo "Pending", las cesiones caían en el
    // histórico de solo lectura y el propietario no tenía forma de aprobarlas.
    const guestPendingRows = useMemo(() => allGuestRows.filter((r) => isUnresolvedAccessRequestStatus(r.statusCode)), [allGuestRows]);
    const ownPendingRows = useMemo(() => allOwnRows.filter((r) => isUnresolvedAccessRequestStatus(r.statusCode)), [allOwnRows]);

    const historyRows: AccessRequestHistoryRow[] = useMemo(() =>
    {
        const resolvedGuest = allGuestRows.filter((r) => !isUnresolvedAccessRequestStatus(r.statusCode));
        const resolvedOwn = allOwnRows.filter((r) => !isUnresolvedAccessRequestStatus(r.statusCode));
        return [...resolvedGuest, ...resolvedOwn].map(currentRowToHistoryRow);
    }, [allGuestRows, allOwnRows]);
    const [roleFilter, setRoleFilter] = useState<RoleFilterValue>("all");
    const [personTypeTab, setPersonTypeTab] = useState<IdentityPersonType>("NaturalPerson");

    // Nombre del plan para el banner del modo contrato: se resuelve del backend (mismo criterio que
    // el resto de pasos), no se arrastra por la navegación. Mientras carga, el banner va sin nombre.
    useEffect(() =>
    {
        let cancelled = false;
        if(contractSavingsPlanId == null)
        {
            setContractSavingsPlanName("");
            return;
        }
        savingsPlans.getSavingsPlan(contractSavingsPlanId).then((plan) =>
        {
            if(!cancelled)
            {
                setContractSavingsPlanName(plan?.name || "");
            }
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setContractSavingsPlanName("");
            }
        });
        return () =>
        {
            cancelled = true;
        };
    }, [contractSavingsPlanId]);

    // Resolución del paso actual del onboarding (backend manda). Separado del nombre para poder
    // reutilizar la recarga tras un avance fallido y volver a decidir si el botón "Continuar" procede.
    const loadContractCurrentStep = useCallback(async (): Promise<void> =>
    {
        if(contractSavingsPlanId == null)
        {
            setContractCurrentStep(null);
            setIsResolvingContractStep(false);
            return;
        }
        setIsResolvingContractStep(true);
        try
        {
            const envelope = await savingsPlans.getPlanOnboardingState(contractSavingsPlanId);
            setContractCurrentStep(envelope.success && envelope.data ? envelope.data.currentStep : null);
        }
        catch
        {
            setContractCurrentStep(null);
        }
        finally
        {
            setIsResolvingContractStep(false);
        }
    }, [contractSavingsPlanId]);

    useEffect(() =>
    {
        let cancelled = false;
        if(contractSavingsPlanId == null)
        {
            setContractCurrentStep(null);
            setIsResolvingContractStep(false);
            return;
        }
        setIsResolvingContractStep(true);
        savingsPlans.getPlanOnboardingState(contractSavingsPlanId).then((envelope) =>
        {
            if(!cancelled)
            {
                setContractCurrentStep(envelope.success && envelope.data ? envelope.data.currentStep : null);
            }
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setContractCurrentStep(null);
            }
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setIsResolvingContractStep(false);
            }
        });
        return () =>
        {
            cancelled = true;
        };
    }, [contractSavingsPlanId]);

    // Si llegamos con un mensaje de éxito en el state de navegación, lo limpiamos para que no
    // reaparezca al refrescar o volver atrás, y programamos su auto-ocultado (comportamiento "toast").
    // El modo contrato SÍ viaja en el state, así que se reinyecta al limpiar el mensaje.
    useEffect(() =>
    {
        if(!savedSuccessMessage && !suitabilityLimitMessage)
        {
            return;
        }
        history.replace(
        {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: contractSavingsPlanId != null ? {contractSavingsPlanId: contractSavingsPlanId} : undefined,
        });
        if(!savedSuccessMessage)
        {
            return;
        }
        const timeoutId = window.setTimeout(() => setSavedSuccessMessage(null), 3000);
        return () => window.clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carga inicial de datos - UNA ÚNICA VEZ por ciclo de componente (array deps [] = solo al montar)
    useEffect(() =>
    {
        let cancelled = false;

        const loadIdentities = async () =>
        {
            const user = Auth.getUserProfile();
            if(!user?.id)
            {
                if(!cancelled)
                {
                    setMainIdentity(null);
                    setIdentities([]);
                    setIsLoading(false);
                }
                return;
            }
            try
            {
                // El 2.1 lista por usuario, no por plan: los links de SavingsPlansUserIdentities
                // los crea el 2.2. GetMainIdentityByUserId cubre el titular mínimo creado al pagar.
                const [byUserEnv, mainEnv] = await Promise.all(
                [
                    UserIdentitiesService.getUserIdentitiesByUserId(user.id),
                    UserIdentitiesService.getMainIdentityByUserId(user.id),
                ]);
                if(cancelled)
                {
                    return;
                }
                const loadedIdentities = byUserEnv.success ? normalizeIdentityListItems(byUserEnv.data) : [];
                const loadedMain = mainEnv.success ? normalizeMainUserIdentityDto(mainEnv.data) : null;
                setMainIdentity(loadedMain);
                setIdentities(mergeMainIntoIdentitiesList(loadedIdentities, loadedMain));
            }
            catch
            {
                if(!cancelled)
                {
                    setMainIdentity(null);
                    setIdentities([]);
                }
            }
            finally
            {
                if(!cancelled)
                {
                    setIsLoading(false);
                }
            }
        };

        void loadIdentities();

        return () =>
        {
            cancelled = true;
        };
    }, []);

    useEffect(() =>
    {
        let cancelled = false;

        const loadAccessRequests = async () =>
        {
            setAccessRequestsLoading(true);
            setAccessRequestsLoadError(null);
            try
            {
                const env = await UserIdentitiesService.getAllAccessRequests();
                if(cancelled)
                {
                    return;
                }
                if(!env.success)
                {
                    setAccessRequestsLoadError(orchestratorErrorsToSingleMessage(env.errors) || "No se pudieron cargar las solicitudes de acceso.");
                    setAllGuestRows([]);
                    setAllOwnRows([]);
                    return;
                }
                const data = env.data;
                if(!data)
                {
                    setAllGuestRows([]);
                    setAllOwnRows([]);
                    return;
                }

                const guestList: AccessRequestItemDto[] = Array.isArray(data.guestRequests) ? data.guestRequests : [];
                const ownList: AccessRequestItemDto[] = Array.isArray(data.ownRequests) ? data.ownRequests : [];

                setAllGuestRows(guestList.map(mapAccessRequestItemToCurrentRow));
                setAllOwnRows(ownList.map(mapAccessRequestItemToCurrentRow));
            }
            catch (err: any)
            {
                if(!cancelled)
                {
                    setAccessRequestsLoadError(err?.message ?? "No se pudieron cargar las solicitudes de acceso.");
                    // eslint-disable-next-line no-console
                    console.error("[getAllAccessRequests] error:", err);
                    setAllGuestRows([]);
                    setAllOwnRows([]);
                }
            }
            finally
            {
                if(!cancelled)
                {
                    setAccessRequestsLoading(false);
                }
            }
        };

        void loadAccessRequests();

        return () =>
        {
            cancelled = true;
        };
    }, []);

    useEffect(() =>
    {
        if(showAccessRequestsModal)
        {
            setAccessRequestDecisionError(null);
        }
    }, [showAccessRequestsModal]);

    useEffect(() =>
    {
        if(!showAccessRequestsModal)
        {
            resetAccessRequestSignatureState();
        }
    }, [showAccessRequestsModal, resetAccessRequestSignatureState]);

    const submitManageAccessRequest = useCallback(async (row: AccessRequestCurrentRow, apiStatus: "Approved" | "Rejected"): Promise<AccessRequestDecisionOutcome> =>
    {
        if(!row.id || !row.userIdentityId)
        {
            setAccessRequestDecisionError("Faltan datos de la solicitud (identificadores).");
            return "final";
        }
        // La firma es parte de la decisión, no un trámite previo: sin ella no se manda nada, porque el backend
        // rechaza la resolución y la solicitud se quedaría pendiente igualmente.
        const signatureId = pendingAccessRequestSignatureIdRef.current;
        if(!signatureId)
        {
            setAccessRequestDecisionError("No se pudo completar la firma de la solicitud. Inténtelo de nuevo.");
            return "final";
        }
        setAccessRequestDecisionError(null);
        setAccessRequestDecisionSubmitting(true);
        try
        {
            const result = await UserIdentitiesService.manageAccessRequestIdentity({
                accessRequestId: row.id,
                userIdentityId: row.userIdentityId,
                status: apiStatus,
                signatureId,
            });
            if(!result.success)
            {
                const msg = orchestratorErrorsToSingleMessage(result.errors) || "No se pudo completar la acción.";
                setAccessRequestDecisionError(msg);
                return classifyAccessRequestDecisionFailure(result.errors);
            }
            const newStatus = apiStatus === "Approved" ? resolveApprovedStatusCode(row.statusCode) : "Rejected";
            const resolvedAt = formatIsoDateTimeToDdMmYyyy(new Date());
            setAllGuestRows((prev) => prev.map((r) => r.id === row.id ? { ...r, statusCode: newStatus, status: formatAccessRequestStatusForDisplay(newStatus), approvalDate: resolvedAt } : r));
            return "ok";
        }
        catch(err: unknown)
        {
            // La llamada ni llegó a dar respuesta: fallo de red, no de la firma. Reintentar es seguro.
            const msg = err instanceof Error ? err.message : "No se pudo completar la acción.";
            setAccessRequestDecisionError(msg);
            return "retryable";
        }
        finally
        {
            setAccessRequestDecisionSubmitting(false);
        }
    }, []);

    const beginAccessRequestDecision = useCallback((row: AccessRequestCurrentRow, apiStatus: "Approved" | "Rejected") =>
    {
        setAccessRequestDecisionError(null);
        setAccessRequestDecisionRetryAvailable(false);
        pendingAccessRequestRowRef.current = row;
        pendingAccessRequestStatusRef.current = apiStatus;
        pendingAccessRequestSignatureIdRef.current = null;
        // Una cesión no es un préstamo: el propietario está firmando que renuncia al interviniente, no que
        // presta acceso. El texto lo dice, porque la firma es lo que deja constancia de lo que decidió.
        const isOwnershipTransfer = row.statusCode === "OwnershipTransferPending";
        setAccessRequestSignatureHeaderText(
            apiStatus === "Approved"
                ? isOwnershipTransfer
                    ? "Comprueba el SMS que has recibido en tu teléfono con la clave para confirmar que cedes la propiedad de este interviniente."
                    : "Comprueba el SMS que has recibido en tu teléfono con la clave para confirmar que aceptas la autorización al interviniente."
                : isOwnershipTransfer
                    ? "Comprueba el SMS que has recibido en tu teléfono con la clave para confirmar que rechazas esta solicitud de cesión de propiedad del interviniente."
                    : "Comprueba el SMS que has recibido en tu teléfono con la clave para confirmar que rechazas esta solicitud de autorización al interviniente.",
        );
        setAccessRequestSignaturePhase(true);
        setAccessRequestSignatureLoading(true);
        setAccessRequestSignatures([]);
        setAccessRequestSignatureUserIdentity(null);
        const userId = Auth.getUserProfile()?.id;
        if(!userId)
        {
            setAccessRequestDecisionError("No se pudo identificar el usuario.");
            resetAccessRequestSignatureState();
            return;
        }
        if(!row.userIdentityId)
        {
            setAccessRequestDecisionError("Faltan datos de la solicitud (identificadores).");
            resetAccessRequestSignatureState();
            return;
        }
        Promise.all([SignaturesService.createSignatureNoPlan(userId, {useOperationPhone: true}), UserIdentitiesService.getMainIdentityByUserId(userId)]).then(([sigResponse, mainEnv]) =>
        {
            const data = (sigResponse as {data?: ISignature | ISignature[]})?.data;
            let sigs: ISignature[] = [];
            if(Array.isArray(data))
            {
                sigs = data;
            }
            else if(data && typeof data === "object")
            {
                sigs = [data];
            }
            if(sigs.length === 0 || sigs.some((s) => s.id == null))
            {
                throw new Error("Firma no disponible.");
            }
            if(!mainEnv.success)
            {
                setAccessRequestDecisionError(orchestratorErrorsToSingleMessage(mainEnv.errors) || "No se pudo obtener el interviniente principal.");
                resetAccessRequestSignatureState();
                return;
            }
            const mainIdentity = mainEnv.data;
            if(!mainIdentity || !("id" in mainIdentity))
            {
                setAccessRequestDecisionError("No se pudo obtener el interviniente principal.");
                resetAccessRequestSignatureState();
                return;
            }
            processAccessRequestExpirationDate(sigs);
            setAccessRequestSignatures(sigs);
            setAccessRequestSignatureUserIdentity(mainIdentity as MainUserIdentityDto);
            // La firma que el usuario va a verificar es la que acompañará a la decisión.
            pendingAccessRequestSignatureIdRef.current = sigs[0].id ?? null;
        })
        .catch(() =>
        {
            setAccessRequestDecisionError("No se pudo iniciar la firma por SMS. Inténtelo de nuevo.");
            resetAccessRequestSignatureState();
        })
        .finally(() =>
        {
            setAccessRequestSignatureLoading(false);
        });
    }, [resetAccessRequestSignatureState]);

    const handleAccessRequestSignatureSigned = useCallback(async (response: unknown) =>
    {
        const signatureResponse = response as {data?: {success?: boolean}};
        const row = pendingAccessRequestRowRef.current;
        const apiStatus = pendingAccessRequestStatusRef.current;
        if(!row || !apiStatus)
        {
            return;
        }
        if(!signatureResponse?.data?.success)
        {
            setAccessRequestDecisionError("No se pudo completar la firma de la solicitud. Inténtelo de nuevo.");
            resetAccessRequestSignatureState();
            return;
        }
        const outcome = await submitManageAccessRequest(row, apiStatus);
        if(outcome === "retryable")
        {
            // Falló la red o el servidor, no la firma: se ofrece reintentar UNA vez con la misma firma
            // (ver handleRetryAccessRequestDecision).
            clearAccessRequestSignatureUi();
            setAccessRequestDecisionRetryAvailable(true);
            return;
        }
        // "ok" o "final": en ambos casos la ceremonia de esta firma ha terminado. En "final" el mensaje de
        // error se queda visible (reset no lo borra) y el usuario, si procede, vuelve a pulsar y recibe un
        // SMS nuevo. Ofrecer aquí un reintento con la misma firma era lo que los dejaba en bucle.
        resetAccessRequestSignatureState();
    }, [resetAccessRequestSignatureState, submitManageAccessRequest, clearAccessRequestSignatureUi]);

    const handleRetryAccessRequestDecision = useCallback(async () =>
    {
        const row = pendingAccessRequestRowRef.current;
        const apiStatus = pendingAccessRequestStatusRef.current;
        if(!row || !apiStatus)
        {
            return;
        }
        await submitManageAccessRequest(row, apiStatus);
        // Un solo reintento por firma, salga como salga. Un fallo transitorio de verdad (timeout, respuesta
        // perdida) se resuelve al primer reintento gracias a la idempotencia del backend. Si vuelve a fallar
        // no es transitorio, aunque el código lo parezca: un 500 determinista llega como el mismo
        // INTERVENER_OPERATION_FAILED que un fallo puntual. Ofrecerlo otra vez es el bucle que se vio en
        // producción (2026-09): la misma firma enviada una y otra vez. El mensaje de error se queda visible.
        resetAccessRequestSignatureState();
    }, [submitManageAccessRequest, resetAccessRequestSignatureState]);

    const handleAccessRequestSignatureNewCodeSended = useCallback((signatures: ISignature[]) =>
    {
        processAccessRequestExpirationDate(signatures);
        setAccessRequestSignatures(signatures);
    }, []);

    const handleAccessRequestSignatureChanged = useCallback((signatures: ISignature[]) =>
    {
        processAccessRequestExpirationDate(signatures);
        setAccessRequestSignatures(signatures);
    }, []);

    const handleCloseAccessRequestsModal = useCallback(() =>
    {
        if(accessRequestSignaturePhase)
        {
            resetAccessRequestSignatureState();
            return;
        }
        setShowAccessRequestsModal(false);
    }, [accessRequestSignaturePhase, resetAccessRequestSignatureState]);

    const handleAcceptAccessRequest = useCallback((row: AccessRequestCurrentRow) =>
    {
        beginAccessRequestDecision(row, "Approved");
    }, [beginAccessRequestDecision]);

    const handleRejectAccessRequest = useCallback((row: AccessRequestCurrentRow) =>
    {
        beginAccessRequestDecision(row, "Rejected");
    }, [beginAccessRequestDecision]);

    // Helpers
    const getFullName = (identity: Identity | null): string =>
    {
        if(!identity)
        {
            return "";
        }
        const fromParts = [identity.firstName, identity.lastName, identity.secondLastName].filter(Boolean).join(" ");
        if(fromParts)
        {
            return fromParts;
        }
        if(identity.fullName && identity.fullName.trim() !== "")
        {
            return identity.fullName;
        }
        return "(Sin nombre)";
    };

    const isExternal = (identity: Identity): boolean => identity.status === UserIdentityStatusEnum.External;

    // Fuente de verdad única: byUser ya trae el estado real del KYC (kycCompleted = documento firmado,
    // resuelto en backend contra Omnia). Sin llamada aparte a la matriz.
    const isKycSigned = (identity: Identity): boolean => identity.kycCompleted;

    // El botón de re-firma standalone aparece SOLO para intervinientes OUTDATED: su contrato ya
    // está formalizado y su KYC quedó invalidado por una edición, así que solo necesitan re-firmar para
    // volver a ACTIVE. Los PENDING NO firman aquí: firman su KYC montado sobre la firma del contrato
    // cuando entran en uno. Separación limpia.
    // Ausente se trata como completo: un front por delante del backend no debe esconder el botón a quien sí puede firmar.
    const canSignKyc = (identity: Identity): boolean =>
        identity.status === UserIdentityStatusEnum.Outdated && identity.kycDataComplete !== false;

    // Recibido por cesión aprobada y sin aceptar: «Editar» llega deshabilitado (canEdit=false), así que la entrada
    // a su edición, que es donde está el formulario de aceptación, es esta acción.
    const isPendingTransferAcceptance = (identity: Identity): boolean =>
        identity.role === "Owner" && isTransferPendingAcceptance(identity.status);

    const needsKycData = (identity: Identity): boolean =>
        identity.status === UserIdentityStatusEnum.Outdated && identity.kycDataComplete === false;

    const handleSignKyc = (identity: Identity): void =>
    {
        // Id por estado de navegación (no en la URL): mismo criterio anti-IDOR que la edición.
        history.push(KycSignaturePath,
        {
            userIdentityId: identity.id,
            userIdentityName: getFullName(identity),
            contractSavingsPlanId: contractSavingsPlanId,
        });
    };

    const handleRenewSuitabilityTest = (identity: Identity): void =>
    {
        const navigationState: SuitabilityTestRenewalNavigationState =
        {
            userIdentityId: identity.id,
            userIdentityName: getFullName(identity),
        };
        history.push(SuitabilityTestRenewalPath, navigationState);
    };

    const handleBackToMap = () =>
    {
        if(contractSavingsPlanId == null)
        {
            return;
        }
        history.push("/configurationMap", buildMapNavigationState(contractSavingsPlanId, contractSavingsPlanName || undefined));
    };

    const mainRow = identities.find((identity) => identity.isMain || (mainIdentity != null && identity.id === mainIdentity.id));
    const mainStatus = mainRow?.status ?? mainIdentity?.status;
    const holderIsNewDraft = isMainNewDraft(mainStatus);
    const canCloseStep21 = isMainSelectableForStep21(mainStatus);
    const canAddIntervener = canAddAdditionalIntervener(mainIdentity);

    const handleContinueContract = async () =>
    {
        if(contractSavingsPlanId == null || !canCloseStep21)
        {
            return;
        }

        setAdvanceError(null);
        setIsAdvancingStep(true);
        try
        {
            // Enviamos el paso que creemos actual: si el backend ve otro (pantalla desincronizada),
            // rechaza con STEP_MISMATCH en vez de avanzar un paso equivocado.
            const result = await savingsPlans.advancePlanStep(contractSavingsPlanId, "2.1");
            if(result.success)
            {
                history.push("/configurationMap", buildMapNavigationState(contractSavingsPlanId, contractSavingsPlanName || undefined));
                return;
            }

            setAdvanceError(resolveContractOnboardingError(result.errors?.[0], ContractOnboardingStrings.continueContractFallbackError));
            // Re-sincronizamos el paso: si falló por desincronía, el botón se ocultará al recalcular.
            void loadContractCurrentStep();
        }
        finally
        {
            setIsAdvancingStep(false);
        }
    };

    // Handlers de navegación
    // IMPORTANTE: Para edición, el ID se pasa por estado de navegación (NO en la URL)
    // Esto cumple con OWASP Broken Access Control - evita IDOR (Insecure Direct Object Reference)
    const handleEdit = (identity: Identity) =>
    {
        // La empresa no tiene formulario de persona física: se edita en el paso 3 de su alta
        if(identity.personType === "LegalEntity" && isCompanyOnboardingEnabled())
        {
            const companyState: CompanyDetailsNavigationState = { companyIdentityId: identity.id };
            history.push(CompanyDetailsPath, companyState);
            return;
        }

        // El id sigue viajando por state (anti-IDOR), igual que el contexto de contrato.
        const navigationState: IdentityNavigationState =
        {
            userIdentityId: identity.id,
            contractSavingsPlanId: contractSavingsPlanId,
        };
        history.push(IdentityEditPath, navigationState);
    };

    const handleAddInterviniente = () =>
    {
        // El titular NEW del pago no es un alta: 0 crearía una segunda fila.
        const mainToComplete = mainRow;
        if(mainToComplete && isMainNewDraft(mainToComplete.status ?? mainIdentity?.status))
        {
            handleEdit(mainToComplete);
            return;
        }
        history.push(IdentityNewPath, {contractSavingsPlanId: contractSavingsPlanId});
    };

    const handleAddMinorOrDependent = () =>
    {
        const mainToComplete = mainRow;
        if(mainToComplete && isMainNewDraft(mainToComplete.status ?? mainIdentity?.status))
        {
            handleEdit(mainToComplete);
            return;
        }
        history.push(IdentityMinorPath, {contractSavingsPlanId: contractSavingsPlanId});
    };

    // Alta de empresa: empieza por la suscripción (paso 2); los representantes se dan de alta aparte o desde el paso 3
    const handleAddCompany = () =>
    {
        history.push(CompanySubscriptionPath);
    };

    // Handlers de modales
    const openModal = (identity: Identity, modalSetter: (value: boolean) => void) =>
    {
        setSelectedIdentity(identity);
        modalSetter(true);
    };

    const closeModal = (modalSetter: (value: boolean) => void) =>
    {
        modalSetter(false);
        setSelectedIdentity(null);
    };

    const openRenounceModal = (identity: Identity) =>
    {
        setRenounceModalError(null);
        setSelectedIdentity(identity);
        setShowRenounceModal(true);
    };

    const openDeleteModal = (identity: Identity) =>
    {
        setDeleteModalError(null);
        setSelectedIdentity(identity);
        setShowDeleteModal(true);
    };

    // Acciones CRUD
    const confirmDelete = async () =>
    {
        if(!selectedIdentity || selectedIdentity.isMain)
        {
            return;
        }
        const userIdentityId = selectedIdentity.id;
        setDeleteModalError(null);
        setDeleteSubmitting(true);
        try
        {
            const result = await UserIdentitiesService.deleteIdentity(userIdentityId);
            if(!result.success)
            {
                setDeleteModalError(orchestratorErrorsToSingleMessage(result.errors) || "No se pudo eliminar el interviniente.");
                return;
            }
            setIdentities((prev) => prev.filter((i) => i.id !== userIdentityId));
            closeModal(setShowDeleteModal);
        }
        catch
        {
            setDeleteModalError("No se pudo eliminar el interviniente. Inténtalo de nuevo.");
        }
        finally
        {
            setDeleteSubmitting(false);
        }
    };

    const confirmRevokeUserAccess = async (guestUserId: number): Promise<RevokeAccessResult> =>
    {
        if(!selectedIdentity)
        {
            return {ok: false, message: "No hay interviniente seleccionado."};
        }
        const userIdentityId = selectedIdentity.id;
        const result = await UserIdentitiesService.revokeUserIdentityAccess({userIdentityId, guestUserId});
        if(!result.success)
        {
            const msg = orchestratorErrorsToSingleMessage(result.errors) || "No se pudo revocar el acceso.";
            return {ok: false, message: msg};
        }
        setIdentities((prev) => prev.map((identity) =>
        {
            if(identity.id !== userIdentityId)
            {
                return identity;
            }
            return{
                ...identity,
                associatedUsers: identity.associatedUsers.filter((u) => u.id !== guestUserId),
            };
        }));
        setSelectedIdentity((prev) => prev && prev.id === userIdentityId ?
            {...prev, associatedUsers: prev.associatedUsers.filter((u) => u.id !== guestUserId)} :
            prev);
        return { ok: true };
    };

    const confirmRenounceAccess = async () =>
    {
        if(!selectedIdentity)
        {
            return;
        }
        const userIdentityId = selectedIdentity.id;
        setRenounceModalError(null);
        setRenounceSubmitting(true);
        try
        {
            const result = await UserIdentitiesService.renounceUserIdentityAccess({userIdentityId});
            if(!result.success)
            {
                setRenounceModalError(orchestratorErrorsToSingleMessage(result.errors) || "No se pudo renunciar al acceso.");
                return;
            }
            setIdentities((prev) => prev.filter((i) => i.id !== userIdentityId));
            closeModal(setShowRenounceModal);
        }
        catch
        {
            setRenounceModalError("No se pudo renunciar al acceso. Inténtalo de nuevo.");
        }
        finally
        {
            setRenounceSubmitting(false);
        }
    };

    // Pestañas por tipología (solo si hay alguna empresa o representante) y, dentro de cada una, el filtro de rol
    const showPersonTypeTabs = shouldShowPersonTypeTabs(identities);
    const identitiesInTab = filterByPersonType(identities, personTypeTab, showPersonTypeTabs);
    const filteredIdentities = roleFilter === "all"
        ? identitiesInTab
        : identitiesInTab.filter((i) => i.role === roleFilter);

    return(
        <DocumentTitle title="IronIA - Gestión de Intervinientes">
            <React.Fragment>
                <HelperStyle />
                <Cont theme={themeContext}>
                    <MainCont>
                        {savedSuccessMessage &&
                        (
                            <MessageBar messageBarType={MessageBarType.success}
                                isMultiline={false}
                                onDismiss={() => setSavedSuccessMessage(null)}
                                dismissButtonAriaLabel="Cerrar"
                                styles={{root: {marginBottom: "16px"}}}
                            >
                                {savedSuccessMessage}
                            </MessageBar>
                        )}
                        {suitabilityLimitMessage &&
                        (
                            <MessageBar messageBarType={MessageBarType.warning}
                                isMultiline={true}
                                onDismiss={() => setSuitabilityLimitMessage(null)}
                                dismissButtonAriaLabel="Cerrar"
                                styles={{root: {marginBottom: "16px"}}}
                            >
                                {suitabilityLimitMessage}
                            </MessageBar>
                        )}
                        {advanceError &&
                        (
                            <MessageBar messageBarType={MessageBarType.error} isMultiline={true}
                                onDismiss={() => setAdvanceError(null)}
                                dismissButtonAriaLabel="Cerrar"
                                styles={{root: {marginBottom: "16px"}}}
                            >
                                {advanceError}
                            </MessageBar>
                        )}
                        <Title>
                            <h1>Gestión de Intervinientes</h1>
                            <TitleSubtitleRow>
                                <h2>Aquí puedes gestionar todos los intervinientes asociados a tu usuario.</h2>
                                <DefaultButton className="button tiny-secondary-button access-requests-toolbar-button"
                                    styles={accessRequestsTriggerButtonStyles}
                                    onClick={() => setShowAccessRequestsModal(true)}
                                    disabled={isLoading}
                                    aria-label={guestPendingRows.length > 0
                                            ? `Solicitudes de acceso, ${guestPendingRows.length} pendientes`
                                            : "Solicitudes de acceso"
                                    }
                                >
                                    <span>Solicitudes de acceso</span>
                                    {!accessRequestsLoadError && guestPendingRows.length > 0 &&
                                    (
                                        <AccessRequestsCountInline aria-hidden="true">
                                            {guestPendingRows.length}
                                        </AccessRequestsCountInline>
                                    )}
                                </DefaultButton>
                            </TitleSubtitleRow>
                        </Title>
                        <BarCont>
                            <BgBar />
                        </BarCont>
                        {showContractBanner &&
                        (
                            <ContractModeBanner role="status">
                                <ContractModeBannerRow>
                                    <ContractModeBannerText>
                                        {ContractOnboardingStrings.identitiesBanner(contractSavingsPlanName)}
                                    </ContractModeBannerText>
                                    <ContractModeBannerActions>
                                        <DefaultButton
                                            text={ContractOnboardingStrings.identitiesBackToMap}
                                            onClick={handleBackToMap}
                                            disabled={isAdvancingStep || isLoading}
                                        />
                                        {canAdvanceContractStep &&
                                        (
                                            <PrimaryButton
                                                text={ContractOnboardingStrings.continueContract}
                                                onClick={handleContinueContract}
                                                disabled={isAdvancingStep || isLoading || !canCloseStep21}
                                            />
                                        )}
                                    </ContractModeBannerActions>
                                </ContractModeBannerRow>
                            </ContractModeBanner>
                        )}
                        {showContractBanner && holderIsNewDraft &&
                        (
                            <MessageBar messageBarType={MessageBarType.info} isMultiline={true}
                                styles={{root: {marginBottom: "16px"}}}
                            >
                                El titular de tu plan ya está creado. Completa y finaliza sus datos para continuar con el contrato.
                            </MessageBar>
                        )}
                        <TabletCont theme={themeContext}>
                            <RoleFilterRow>
                                {showPersonTypeTabs &&
                                (
                                    <PersonTypePivotWrap>
                                        <Pivot selectedKey={personTypeTab} onLinkClick={(item) => item?.props.itemKey && setPersonTypeTab(item.props.itemKey as IdentityPersonType)}>
                                            {PersonTypeTabs.map((tab) => (
                                                <PivotItem key={tab.key} itemKey={tab.key} headerText={tab.title} itemCount={countByPersonType(identities, tab.key)} />
                                            ))}
                                        </Pivot>
                                    </PersonTypePivotWrap>
                                )}
                                <RoleFilterDropdown value={roleFilter} onChange={setRoleFilter} />
                            </RoleFilterRow>
                            <Table>
                                <thead>
                                    <tr>
                                        <th className="wide">Nombre y apellidos</th>
                                        <th className="wide">Correo electrónico</th>
                                        <th className="tight">Menor de edad</th>
                                        <th className="tight">Conocer a nuestro cliente (KYC)</th>
                                        <th className="tight">Test de idoneidad</th>
                                        <th className="tight">Solicitudes de acceso</th>
                                        <th className="tight">Planes de Ahorro</th>
                                        <th className="tight">Usuarios Autorizados</th>
                                        <th className="tight">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ?
                                    (
                                        <tr>
                                            <td colSpan={9} style={{ textAlign: "center", padding: "40px" }}>
                                                <Spinner size={SpinnerSize.large} />
                                            </td>
                                        </tr>
                                    ) : 
                                    (filteredIdentities.map((identity) =>
                                    {
                                        const suitabilityAction = getSuitabilityTestAction(identity, isContractMode);
                                        return(
                                        <tr key={identity.id}>
                                            <td>
                                                {getFullName(identity)}
                                                {isPendingTransferAcceptance(identity) &&
                                                (
                                                    <div>
                                                        <PendingTransferBadge title="Aceptar cesión" onClick={() => handleEdit(identity)}>
                                                            Cesión pendiente de aceptar
                                                        </PendingTransferBadge>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{identity.email}</td>
                                            <td className="text-center">
                                                {identity.isMinor ? "Sí" : "No"}
                                            </td>
                                            <td className="text-center">
                                                {isKycSigned(identity) ? "Sí" : "No"}
                                            </td>
                                            <td className="text-center">
                                                {getSuitabilityTestLabel(identity)}
                                            </td>
                                            <td className="text-center">
                                                <RoleBadge role={identity.role}>
                                                    {identity.role === "Owner" ? "Propietario" : "Solo lectura"}
                                                </RoleBadge>
                                            </td>
                                            <td className="text-center">
                                                {identity.savingsPlans.length > 0 ?
                                                (
                                                    <SavingsPlansLink onClick={() => openModal(identity, setShowSavingsPlansModal)}>
                                                        {identity.savingsPlans.length} {identity.savingsPlans.length === 1 ? 'plan' : 'planes'}
                                                    </SavingsPlansLink>
                                                ) :
                                                (
                                                    <span>-</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {!isExternal(identity) && identity.associatedUsers.length > 0 ?
                                                (
                                                    <SavingsPlansLink onClick={() => openModal(identity, setShowAssociatedUsersModal)}>
                                                        {identity.associatedUsers.length} {identity.associatedUsers.length === 1 ? 'usuario' : 'usuarios'}
                                                    </SavingsPlansLink>
                                                ) :
                                                (
                                                    <span>-</span>
                                                )}
                                            </td>
                                            <td className="actions">
                                                {canSignKyc(identity) && !isContractMode &&
                                                (
                                                    <SignKycIcon className="ms-Icon ms-Icon--PenWorkspace" title="Firmar KYC" onClick={() => handleSignKyc(identity)} />
                                                )}
                                                {needsKycData(identity) && !isContractMode &&
                                                (
                                                    <SignKycIcon className="ms-Icon ms-Icon--Warning" title="Completa sus datos para poder firmar el KYC" onClick={() => handleEdit(identity)} />
                                                )}
                                                {suitabilityAction.visible &&
                                                (
                                                    <SignKycIcon
                                                        className="ms-Icon ms-Icon--Refresh"
                                                        title={suitabilityAction.title}
                                                        aria-disabled={suitabilityAction.disabled}
                                                        style={suitabilityAction.disabled
                                                            ? {opacity: 0.4, cursor: "not-allowed", animation: "none"}
                                                            : undefined}
                                                        onClick={suitabilityAction.disabled
                                                            ? undefined
                                                            : () => handleRenewSuitabilityTest(identity)}
                                                    />
                                                )}
                                                {isPendingTransferAcceptance(identity) &&
                                                (
                                                    <CustomIcon iconName="Accept" size="17" color="#CC214F" paddingTop="3%" title="Aceptar cesión" onClick={() => handleEdit(identity)} />
                                                )}
                                                {!isExternal(identity) && identity.canEdit !== false && !isPendingTransferAcceptance(identity) &&
                                                (
                                                    <CustomIcon iconName="Edit" size="17" color="#CC214F" paddingTop="3%" title="Editar" onClick={() => handleEdit(identity)} />
                                                )}
                                                {!isExternal(identity) && identity.canDelete && !identity.isMain &&
                                                (
                                                    <CustomIcon iconName="Delete" size="17" color="#CC214F" paddingTop="3%" title="Eliminar" onClick={() => openDeleteModal(identity)} />
                                                )}
                                                {!isExternal(identity) && identity.role === "Guest" && identity.canRenounce &&
                                                (
                                                    <CustomIcon iconName="UserRemove" size="17" color="#CC214F" paddingTop="3%" title="Renunciar al acceso" onClick={() => openRenounceModal(identity)} />
                                                )}
                                            </td>
                                        </tr>
                                        );
                                    }))}
                                </tbody>
                            </Table>
                            <ButtonsContainer>
                                <div className="add-buttons">
                                    {holderIsNewDraft && mainRow &&
                                    (
                                        <PrimaryButton className="button tiny-secondary-button" onClick={() => handleEdit(mainRow)} disabled={isLoading}>
                                            Completar titular
                                        </PrimaryButton>
                                    )}
                                    <DefaultButton className="button tiny-secondary-button" onClick={handleAddInterviniente} disabled={isLoading || !canAddIntervener}>
                                        Añadir interviniente
                                    </DefaultButton>
                                </div>
                                <div className="special-buttons">
                                    <p>Añadir intervinientes especiales:</p>
                                    <DefaultButton className="button tiny-secondary-button" onClick={handleAddMinorOrDependent} disabled={isLoading || !canAddIntervener}>
                                        Añadir Menor o Dependiente
                                    </DefaultButton>
                                    {/* Oculto sin el interruptor del alta de empresas (PRO). Desactivado hasta tener el pago del
                                        paso 2; después: isLoading || !canAddIntervener */}
                                    {isCompanyOnboardingEnabled() &&
                                    (
                                        <DefaultButton className="button tiny-secondary-button" onClick={handleAddCompany} disabled={true}>
                                            Añadir Empresa
                                        </DefaultButton>
                                    )}
                                </div>
                            </ButtonsContainer>
                        </TabletCont>
                    </MainCont>
                </Cont>
                {/* Modales */}
                <ModalAccessRequests
                    isOpen={showAccessRequestsModal}
                    onClose={handleCloseAccessRequestsModal}
                    guestPendingRows={guestPendingRows}
                    ownPendingRows={ownPendingRows}
                    historyRows={historyRows}
                    accessRequestsLoading={accessRequestsLoading}
                    accessRequestsLoadError={accessRequestsLoadError}
                    accessRequestDecisionSubmitting={accessRequestDecisionSubmitting}
                    accessRequestDecisionError={accessRequestDecisionError}
                    accessRequestSignaturePhase={accessRequestSignaturePhase}
                    accessRequestSignatureLoading={accessRequestSignatureLoading}
                    accessRequestSignatures={accessRequestSignatures}
                    accessRequestSignatureUserIdentity={accessRequestSignatureUserIdentity}
                    accessRequestSignatureHeaderText={accessRequestSignatureHeaderText}
                    onAccessRequestSignatureSigned={handleAccessRequestSignatureSigned}
                    onAccessRequestSignatureCancel={resetAccessRequestSignatureState}
                    onAccessRequestSignatureNewCodeSended={handleAccessRequestSignatureNewCodeSended}
                    onAccessRequestSignatureChanged={handleAccessRequestSignatureChanged}
                    accessRequestDecisionRetryAvailable={accessRequestDecisionRetryAvailable}
                    onRetryAccessRequestDecision={handleRetryAccessRequestDecision}
                    onAcceptRequest={handleAcceptAccessRequest}
                    onRejectRequest={handleRejectAccessRequest}
                />
                <ModalDeleteIdentity
                    isOpen={showDeleteModal}
                    identityFullName={getFullName(selectedIdentity)}
                    errorMessage={deleteModalError}
                    isSubmitting={deleteSubmitting}
                    isContractMode={isContractMode}
                    onConfirm={confirmDelete}
                    onCancel={() =>
                    {
                        setDeleteModalError(null);
                        closeModal(setShowDeleteModal);
                    }}
                />
                <ModalRenounceAccess
                    isOpen={showRenounceModal}
                    identityFullName={getFullName(selectedIdentity)}
                    errorMessage={renounceModalError}
                    isSubmitting={renounceSubmitting}
                    onConfirm={confirmRenounceAccess}
                    onCancel={() =>
                    {
                        setRenounceModalError(null);
                        closeModal(setShowRenounceModal);
                    }}
                />
                <ModalSavingsPlans
                    isOpen={showSavingsPlansModal}
                    identity={selectedIdentity}
                    identityFullName={getFullName(selectedIdentity)}
                    onClose={() => closeModal(setShowSavingsPlansModal)}
                />
                <ModalAssociatedUsers
                    isOpen={showAssociatedUsersModal}
                    identity={selectedIdentity}
                    identityFullName={getFullName(selectedIdentity)}
                    onClose={() => closeModal(setShowAssociatedUsersModal)}
                    onRevokeAccess={confirmRevokeUserAccess}
                />
            </React.Fragment>
        </DocumentTitle>
    );
};

export default IdentitiesMainScreen;